import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageTitle from '../headers/PageTitle';
import { LaunchScenarioDialog } from './LaunchScenarioDialog';
import { ScenarioEditor } from './ScenarioEditor';
import { ScenariosTable } from './ScenariosTable';
import { useScenarios, type Scenario } from 'hooks/scenarios/scenarioHooks';

/**
 * Saved responses an operator can fire in one click.
 *
 * The editor opens in place rather than in a modal: a scenario carries an
 * audience, a message, a follow-up ladder and a checklist, which is more than a
 * dialog can show without scrolling away from the thing being edited.
 */
const ScenariosPage: React.FC = () => {
  const { data, isLoading, isError } = useScenarios();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ scenario: Scenario | null } | null>(
    null
  );
  const [launching, setLaunching] = useState<Scenario | null>(null);

  const scenarios = useMemo(() => data ?? [], [data]);
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return scenarios;
    return scenarios.filter(
      (scenario) =>
        scenario.name.toLowerCase().includes(term) ||
        (scenario.description ?? '').toLowerCase().includes(term)
    );
  }, [scenarios, search]);

  if (editing) {
    return (
      <div className="p-4 pt-0">
        <PageTitle
          title={editing.scenario ? editing.scenario.name : 'New Scenario'}
        />
        <ScenarioEditor
          scenario={editing.scenario}
          onClose={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="p-4 pt-0">
      <div className="d-flex align-items-start justify-content-between">
        <div>
          <PageTitle title="Scenarios" />
          <p className="text-muted mb-4">
            A saved response: who to reach, what to tell them, and what work to
            create. Launch one from here, from an incident, or from an org
            event&apos;s checklist.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          className="tw:gap-1"
          onClick={() => setEditing({ scenario: null })}
        >
          <Plus className="tw:size-4" />
          New Scenario
        </Button>
      </div>

      {isError ? (
        <p className="text-danger">
          Scenarios could not be loaded. You may not have permission to manage
          them for this organization.
        </p>
      ) : (
        <ScenariosTable
          scenarios={visible}
          search={search}
          onSearchChange={setSearch}
          onEdit={(scenario) => setEditing({ scenario })}
          onLaunch={setLaunching}
          emptyMessage={
            isLoading
              ? 'Loading scenarios…'
              : search.trim()
                ? `No scenarios match “${search}”.`
                : 'No scenarios yet. Create one to save a response you run often.'
          }
        />
      )}

      <LaunchScenarioDialog
        scenario={launching}
        onClose={() => setLaunching(null)}
      />
    </div>
  );
};

export default ScenariosPage;
